import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import api from 'api';
import confirm from 'modules/confirm';
import {
  confirmSaveStageChanges,
  RELEASE_ORDER_CONFIRM_CLASS,
} from 'modules/releaseOrderConfirm';
import classNames from 'classnames';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import InputField from 'components/InputField';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { CheckboxGroupPlaceholders } from './checkboxplaceholders';
import {
  FILES_AVAILABILITY_MODE,
  STEP_COMPLETION_ACTION,
  StepWorkflowConfiguration,
  WorkflowAutomationValues,
} from 'models/WorkflowConfiguration';
import useSpecificRules from '../../hooks/useSpecificRules';
import useStageFilesTree from '../../hooks/useStageFilesTree';
import useDisabledNodeReason from '../../hooks/useDisabledNodeReason';
import DealContext from '../../../../providers/Deal';
import {
  getArrayLength,
  getDataRoomRoute,
  getHasElementsInArray,
  getIsArrayEmpty,
} from 'utils';
import AutomationProgresser from '../AutomationProgresser';
import StageFilesTree from '../StageFilesTree';
import StageSelectedFiles from '../StageSelectedFiles';
import StageFilesOrder from '../StageFilesOrder';
import FilesReachSelector from '../FilesReachSelector';
import WorkflowGuideAnchor from '../WorkflowGuideAnchor';
import UnsavedChangesGuard from 'components/UnsavedChangesGuard';
import arrayMutators from 'final-form-arrays';
import { ADD_NEW_ANSWER_INITIAL } from '../AutomationQuestion/AutomationQuestion.component';
import {
  extractQuestionUpdateValues,
  extractRulesUpdateValues,
  getIsCompletionAction,
} from './utils';
import {
  getCheckedFileIds,
  getDocumentsAvailabilityChanges,
  getIsSameSelection,
  getOrderedStageEntries,
  getSelectedStageEntries,
  toOrderFormItems,
  toOrderPayload,
} from '../../utils/stageFiles';
import { FILES_SELECTOR, RULES_SELECTOR } from '../../data';
import isEqual from 'lodash/isEqual';
import { SUCCESS_MESSAGES } from 'request-responses';
import FloatingActions from 'components/FloatingActions';
import SegmentedToggle from 'components/SegmentedToggle';
import useMatchMedia from 'hooks/useMatchMedia';
import useQueryParams from 'hooks/useQueryParams';
import { DATA_ROOM_LOGIN_SOURCE } from 'models/DataRoomLoginSource';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  AUTOMATION_RELEASE_MODE,
  AutomationStageOpenedEventProps,
} from 'models/MixpanelEvents';

import './WorkflowTemplate.styles.scss';
import './WorkflowTemplate.styles.responsive.scss';

const MOBILE_TABS = {
  FILES: 'files',
  SELECTED: 'selected',
  COMPLETION: 'completion',
} as const;

type MobileTab = typeof MOBILE_TABS[keyof typeof MOBILE_TABS];

type WorkflowTemplateProps = {
  className?: string;
  page: LeadStages;
};

const WorkflowTemplate: React.FC<WorkflowTemplateProps> = ({
  className,
  page,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<StepWorkflowConfiguration>();

  // A search shortens Files, so Selected stops matching its height meanwhile.
  const [isSearchingFiles, setIsSearchingFiles] = useState(false);

  const {
    workflowConfigurationStep,
    dataRoomNodes,
    checkedFilesIds,
    orderedItems,
    filesAvailabilityMode,
    partWayLeadsCount,
    leadsWithFilesCount,
    receivedFileIds,
  } = data || {};

  const {
    automaticallySendNDA,
    automaticallySendNDAEnabled,
    maxCapacity,
    maxCapacityEnabled,
    completionAction,
    progressionQuestion,
  } = workflowConfigurationStep || {};

  const { question, answers } = progressionQuestion || {};

  const isLastStage = page === LEAD_STAGES.OFFER;

  const getMixpanelProps = useGetMixpanelProps();

  // Kept apart, so new message keys are made only when the saved order changes.
  const initialOrderItems = useMemo(
    () => toOrderFormItems(orderedItems, checkedFilesIds),
    [checkedFilesIds, orderedItems],
  );

  const hasData = !!data;

  const automationInitialValues = useMemo(
    () => ({
      maxCapacity,
      specificRules: { automaticallySendNDA },
      // The last stage has no "Move automatically" option to show as picked.
      completionAction:
        !completionAction ||
        (isLastStage &&
          completionAction === STEP_COMPLETION_ACTION.AUTO_ADVANCE)
          ? STEP_COMPLETION_ACTION.KEEP
          : completionAction,
      progressionQuestion: {
        question,
        answers: getHasElementsInArray(answers)
          ? answers.map(({ text, advancesLead }) => ({
              text,
              advancesLead,
            }))
          : [ADD_NEW_ANSWER_INITIAL],
      },
      // Kept in saved order, which is the sequence in "One at a time".
      orderItems: initialOrderItems,
      // Nothing is selected until the stage loads.
      filesAvailabilityMode: hasData
        ? filesAvailabilityMode || FILES_AVAILABILITY_MODE.ALL_AT_ONCE
        : undefined,
    }),
    [
      answers,
      automaticallySendNDA,
      completionAction,
      filesAvailabilityMode,
      hasData,
      initialOrderItems,
      isLastStage,
      maxCapacity,
      question,
    ],
  );

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    canAccessDataRoom,
    dealRoom,
    listing: { envelopeTemplateName },
    workflowConfiguration: { _id: workflowId },
    setWorkflowConfiguration,
  } = useContext(ListingContext);
  const { leadStagesCopies } = useContext(DealContext);
  const classes = classNames(className, 'workflow-template');

  const { _id: dealId, name: dealName } = dealRoom || {};

  const { t } = useTranslation();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const stage = t(leadStagesCopies[page]);

  const tree = useStageFilesTree({ workflowId, page, dataRoomNodes });

  const getDisabledReason = useDisabledNodeReason({ workflowId, page });

  const hasFiles = getHasElementsInArray(tree.rootNodes);

  const dataRoomUrl = useMemo(
    () =>
      getDataRoomRoute(
        dealRoom?.dataRoomId,
        'documents',
        DATA_ROOM_LOGIN_SOURCE.PORTFOLIO,
      ),
    [dealRoom?.dataRoomId],
  );

  // The tab lives in the URL so the guide tour can switch it.
  const {
    params: { tab: activeTab = MOBILE_TABS.FILES },
    setQueryParam,
  } = useQueryParams<{ tab: MobileTab }>();

  // Every section stays mounted so unsaved edits survive a tab switch.
  const hasMobileTabs = !isAbovePhablet && canAccessDataRoom;

  const getSectionClasses = useCallback(
    (section: string, tab: MobileTab) =>
      classNames(`workflow-template__${section}-section`, {
        'workflow-template__section--hidden':
          hasMobileTabs && activeTab !== tab,
      }),
    [activeTab, hasMobileTabs],
  );

  const specificRulesOptions = useSpecificRules({
    automaticallySendNDA,
    automaticallySendNDAEnabled,
    envelopeTemplateName,
  });

  const hasSpecificRules = useMemo(
    () => getHasElementsInArray(specificRulesOptions),
    [specificRulesOptions],
  );

  const showRules = hasSpecificRules || page !== LEAD_STAGES.ENQUIRE;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.portfolio.seller.getWorkflowConfiguration(
          workflowId,
          page,
        );

        setData(data);

        const releaseMode =
          data.filesAvailabilityMode || FILES_AVAILABILITY_MODE.ALL_AT_ONCE;

        mixpanelService.automationStageOpened(
          getMixpanelProps({
            dealId,
            dealName,
            stageName: stage,
            releaseMode: AUTOMATION_RELEASE_MODE[releaseMode],
          }) as AutomationStageOpenedEventProps,
        );
      } catch (err) {
        popServerError(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    workflowId,
    dealId,
    dealName,
    stage,
    popServerError,
    getMixpanelProps,
  ]);

  const onSubmitAutomation = useCallback(
    async (values: WorkflowAutomationValues) => {
      try {
        const {
          orderItems,
          filesAvailabilityMode: mode,
          specificRules,
          maxCapacity,
          ...completionValues
        } = values;
        const {
          orderItems: initialItems,
          filesAvailabilityMode: initialMode,
          specificRules: initialSpecificRules,
          maxCapacity: initialMaxCapacity,
          ...initialCompletionValues
        } = automationInitialValues;

        const isOneAtATimeMode = mode === FILES_AVAILABILITY_MODE.ONE_AT_A_TIME;

        // The last stage shows its completion step only in "One at a time".
        const hasCompletionStep = !isLastStage || isOneAtATimeMode;

        const checkedIds = getCheckedFileIds(orderItems);
        const orderPayload = toOrderPayload(orderItems);

        const dataRoomDocumentsAvailability = getDocumentsAvailabilityChanges(
          checkedIds,
          getCheckedFileIds(initialItems),
        );

        const receivedIds = new Set(receivedFileIds);
        const removedReceivedCount = getArrayLength(
          dataRoomDocumentsAvailability.filter(
            ({ dataRoomNodeId, checked }) =>
              !checked && receivedIds.has(dataRoomNodeId),
          ),
        );

        if (
          removedReceivedCount &&
          !(await confirmSaveStageChanges(removedReceivedCount))
        )
          return;

        // Only what changed is sent, so one part's save never touches the others.
        const updateValues = {
          ...extractRulesUpdateValues(
            { specificRules, maxCapacity },
            {
              specificRules: initialSpecificRules,
              maxCapacity: initialMaxCapacity,
            },
          ),
          ...(hasCompletionStep &&
            !isEqual(completionValues, initialCompletionValues) &&
            extractQuestionUpdateValues(values, isLastStage)),
          ...(getHasElementsInArray(dataRoomDocumentsAvailability) && {
            dataRoomDocumentsAvailability,
          }),
          // Soft signals for the Data Room follow the mode on the BE side.
          ...(mode !== initialMode && { filesAvailabilityMode: mode }),
          // The order and its messages outlive a switch to "Everything at once", so any change sends it whole.
          ...(!getIsSameSelection(orderItems, initialItems, true) && {
            orderedItems: orderPayload,
          }),
        };

        const { data } = await api.portfolio.seller.saveWorkflowConfiguration(
          workflowId,
          page,
          updateValues,
        );

        setData((prev) => ({
          ...prev,
          ...data,
          checkedFilesIds: data.checkedFilesIds ?? checkedIds,
          orderedItems: data.orderedItems ?? orderPayload,
          filesAvailabilityMode: data.filesAvailabilityMode ?? mode,
        }));

        if (mode !== initialMode)
          setWorkflowConfiguration((prev) => ({
            ...prev,
            filesAvailabilityMode: data.filesAvailabilityMode ?? mode,
          }));

        // An empty order counts as complete, so the question comes right away.
        if (
          !isOneAtATimeMode &&
          getIsCompletionAction(values.completionAction) &&
          getIsArrayEmpty(checkedIds)
        ) {
          confirm({
            title: t('noFilesAdded'),
            content: t('noFilesAddedDescription'),
            submitBtnText: t('understood'),
            onlyOneOption: true,
          });
          return;
        }

        popSuccess(t(SUCCESS_MESSAGES.UPDATED_WORKFLOW_CONFIGURATION));
      } catch (e) {
        popServerError(e);
      }
    },
    [
      automationInitialValues,
      page,
      receivedFileIds,
      isLastStage,

      workflowId,
      t,
      popServerError,
      popSuccess,
      setWorkflowConfiguration,
    ],
  );

  return (
    <div className={classes}>
      <Form
        onSubmit={onSubmitAutomation}
        initialValues={automationInitialValues}
        mutators={{ ...arrayMutators }}
        render={({ submitting, values, handleSubmit, form }) => {
          const isOneAtATime =
            values.filesAvailabilityMode ===
            FILES_AVAILABILITY_MODE.ONE_AT_A_TIME;

          const { orderItems, ...restValues } = values;
          const { orderItems: initialItems, ...initialRestValues } =
            automationInitialValues;

          const selectedEntries = getSelectedStageEntries(
            tree,
            new Set(getCheckedFileIds(orderItems)),
          );
          const orderedEntries = getOrderedStageEntries(tree, orderItems);
          const listCount = getArrayLength(
            isOneAtATime ? orderedEntries : selectedEntries,
          );
          const listTitle = isOneAtATime ? t('order') : t('selected');
          const listLabel = listCount
            ? `${listTitle} (${listCount})`
            : listTitle;

          // Messages count only in "One at a time", and only once they have text.
          const isDirty =
            !isEqual(initialRestValues, restValues) ||
            !getIsSameSelection(orderItems, initialItems, isOneAtATime);

          return (
            <form onSubmit={handleSubmit} className="workflow-template__form">
              {canAccessDataRoom && (
                <FilesReachSelector
                  disabled={isLoading}
                  partWayLeadsCount={partWayLeadsCount}
                  leadsWithFilesCount={leadsWithFilesCount}
                />
              )}

              {hasMobileTabs && (
                <SegmentedToggle
                  className="workflow-template__tabs"
                  variant="default"
                  options={[
                    { label: t('files'), value: MOBILE_TABS.FILES },
                    { label: listLabel, value: MOBILE_TABS.SELECTED },
                    { label: t('completion'), value: MOBILE_TABS.COMPLETION },
                  ]}
                  selectedValue={activeTab}
                  onChange={(value) => setQueryParam('tab', value as MobileTab)}
                />
              )}

              {canAccessDataRoom && (
                <div
                  className={classNames('workflow-template__files', {
                    'workflow-template__files--aligned': !isSearchingFiles,
                  })}
                >
                  <section
                    className={getSectionClasses('files', MOBILE_TABS.FILES)}
                  >
                    {isAbovePhablet && (
                      <header className="workflow-template__section-header">
                        <p className="wft-title">
                          {t('files')}
                          <WorkflowGuideAnchor
                            id={FILES_SELECTOR}
                            title="pickStageFiles"
                            description="pickStageFilesDescription"
                          />
                        </p>
                        <p>{t('stageFilesDescription')}</p>
                      </header>
                    )}

                    <StageFilesTree
                      tree={tree}
                      dataRoomUrl={dataRoomUrl}
                      isLoading={isLoading}
                      getDisabledReason={getDisabledReason}
                      onSearchChange={setIsSearchingFiles}
                    />

                    {isOneAtATime && hasFiles && (
                      <p className="workflow-template__description">
                        {t('folderTickAddsNestedFiles')}
                      </p>
                    )}
                  </section>

                  <section
                    className={getSectionClasses(
                      'selected',
                      MOBILE_TABS.SELECTED,
                    )}
                  >
                    {isOneAtATime
                      ? isAbovePhablet && (
                          <header className="workflow-template__section-header">
                            <p className="wft-title">{listLabel}</p>
                            <p>{t('dragToReorderAddMessage')}</p>
                          </header>
                        )
                      : (isAbovePhablet || !!listCount) && (
                          <header className="workflow-template__section-header">
                            {isAbovePhablet && (
                              <p className="wft-title">{listLabel}</p>
                            )}
                            <p>{t('selectedFilesUnlockTogether', { stage })}</p>
                          </header>
                        )}

                    {/* Numbers would shift as nested files load, so the order waits for the whole tree. */}
                    {isLoading || (isOneAtATime && tree.isLoading) ? (
                      <CheckboxGroupPlaceholders itemCount={2} />
                    ) : isOneAtATime ? (
                      <StageFilesOrder
                        entries={orderedEntries}
                        hasFiles={hasFiles}
                      />
                    ) : (
                      <StageSelectedFiles
                        entries={selectedEntries}
                        stage={stage}
                        hasFiles={hasFiles}
                      />
                    )}
                  </section>
                </div>
              )}

              <section
                className={getSectionClasses(
                  'completion',
                  MOBILE_TABS.COMPLETION,
                )}
              >
                <AutomationProgresser page={page} isLoading={isLoading} />
              </section>

              {showRules && (
                <section
                  className={getSectionClasses('rules', MOBILE_TABS.COMPLETION)}
                >
                  {/* Without a completion step, the rules carry the tour anchor. */}
                  <p className="wft-title">
                    {t('stageRules')}
                    {isLastStage && !isOneAtATime && (
                      <WorkflowGuideAnchor
                        id={RULES_SELECTOR}
                        title="setStageRules"
                        description="setStageRulesDescription"
                      />
                    )}
                  </p>
                  <p className="workflow-template__description">
                    {t('setRulesThatApply')}
                  </p>

                  {isLoading ? (
                    <CheckboxGroupPlaceholders />
                  ) : hasSpecificRules ? (
                    <CheckboxFieldGroup
                      name="specificRules"
                      options={specificRulesOptions}
                    />
                  ) : null}

                  {page !== LEAD_STAGES.ENQUIRE && (
                    <Field
                      name="maxCapacity"
                      id="maxCapacity"
                      component={InputField}
                      label={t('setMaxCapacity')}
                      disabled={!maxCapacityEnabled}
                    />
                  )}
                </section>
              )}

              <UnsavedChangesGuard
                className={RELEASE_ORDER_CONFIRM_CLASS}
                when={isDirty && !submitting}
                title={t('leaveStageWithoutSaving')}
                content={t('stageChangesNotSaved')}
                submitBtnText={t('leave')}
                cancelBtnText={t('keepEditing')}
              />

              <FloatingActions
                className="workflow-template__save-bar"
                isVisible={isDirty}
                disabled={submitting}
                message={t(
                  isAbovePhablet
                    ? 'unsavedAutomationChanges'
                    : 'unsavedChangesShort',
                )}
                submitTxt={t(isAbovePhablet ? 'saveChanges' : 'save')}
                onCancel={() => {
                  form.reset();
                  form.restart();
                }}
              />
            </form>
          );
        }}
      />
    </div>
  );
};
export default WorkflowTemplate;
